import { useParams, Link, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { th } from 'date-fns/locale'
import { Card } from '@/common/ui/card'

import { cn } from '@/common/lib/utils'
import { DepositStatus, ManualDepositType } from '@/schema'
import useQueryDepositTransactionsById from '../../api/hooks/useQueryDepositTransactionsById'
import { Icons } from '@/common/ui/icons'
import { Separator } from '@/common/ui/separator'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { Button } from '@/common/ui/button'
import { useState } from 'react'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/common/ui/table'

export default function DepositByIdListing() {
	const { id } = useParams<{ id: string }>()
	const { data: deposit, isLoading } = useQueryDepositTransactionsById(id!)
	const navigate = useNavigate()
	const [showTransactionId, setShowTransactionId] = useState(false)

	const formatDate = (date: string) => {
		return format(new Date(date), 'PPP HH:mm น.', { locale: th })
	}

	const getStatusText = (status: string) => {
		const statusMap: Record<string, string> = {
			CREATED: 'สร้างแล้ว',
			PENDING: 'รอดำเนินการ',
			PROCESSING: 'กำลังตรวจสอบ',
			COMPLETED: 'สำเร็จ',
			FAILED: 'ไม่สำเร็จ',
			CANCELED: 'ยกเลิก',
			EXPIRED: 'หมดอายุ',
			MANUAL_COMPLETED: 'รอธนาคารออโต้ตรวจสอบ',
			PAYMENT_GATEWAY_FAILED: 'กรุณาตรวจสอบกับธนาคารออโต้',
		}
		return statusMap[status] || status
	}

	const getStatusColor = (status: string) => {
		const statusColors: Record<string, string> = {
			CREATED: 'text-blue-500',
			PENDING: 'text-yellow-500',
			PROCESSING: 'text-amber-500',
			COMPLETED: 'text-green-500',
			FAILED: 'text-red-500',
			CANCELED: 'text-red-500',
			EXPIRED: 'text-red-500',
			MANUAL_COMPLETED: 'text-green-500',
			PAYMENT_GATEWAY_FAILED: 'text-red-500',
		}
		return statusColors[status] || 'text-gray-500'
	}

	const getMethodText = (method: string) => {
		const methodMap: Record<string, string> = {
			PAYMENT_GATEWAY: 'ธนาคารออโต้',
			SMS_AUTO: 'SMS Auto',
			VERIFY_SLIP: 'Verify Slip',
			AUTOSSLIP: 'Auto Slip',
			TRUE_WALLET: 'True Wallet',
		}
		return methodMap[method] || 'ธนาคาร'
	}

	const getBankValidateMethodText = (method: string | undefined) => {
		if (!method) return '-'
		const methodMap: Record<string, string> = {
			SMS: 'SMS',
			LINE: 'LINE',
			BANK_API: 'API ธนาคาร',
			BANK_APP: 'แอปธนาคาร',
		}
		return methodMap[method] || method
	}

	const getManualDepositTypeText = (manualDepositType: string | undefined) => {
		if (!manualDepositType) return '-'
		return manualDepositType === ManualDepositType.REGULAR
			? 'ฝากแมนนวล'
			: 'ฟรีเครดิต'
	}

	if (isLoading) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="flex items-center gap-2">
					<Icons.spinner className="h-5 w-5 animate-spin" />
					<span className="text-lg">กำลังโหลด...</span>
				</div>
			</div>
		)
	}

	if (!deposit) {
		return (
			<div className="flex h-screen items-center justify-center">
				<div className="flex items-center gap-2">
					<Icons.warning className="h-5 w-5 text-yellow-500" />
					<span className="text-lg">ไม่พบข้อมูล</span>
				</div>
			</div>
		)
	}

	return (
		<div className="p-6">
			<div className="mb-6 flex items-center justify-between">
				<h1 className="text-2xl font-bold">รายละเอียดการฝากเงิน</h1>
				<Button
					onClick={() => navigate(-1)}
					variant="outline"
					className="flex items-center gap-2 rounded-md border px-4 py-2 hover:bg-gray-100"
				>
					<ArrowLeft className="h-4 w-4" />
					ย้อนกลับ
				</Button>
			</div>
			<div className="grid gap-6 md:grid-cols-2">
				{/* Transaction Info */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.billing className="h-5 w-5" />
							ข้อมูลธุรกรรม
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">
										รหัสธุรกรรมอ้างอิงธนาคารออโต้
									</p>
									<p className="font-medium">{deposit.bankTxId || '-'}</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div className="flex-1">
									<p className="text-sm text-gray-500">รหัสธุรกรรมภายใน</p>
									<div className="flex items-center gap-2">
										<p className="font-medium">
											{showTransactionId ? id : '••••••••••••••••'}
										</p>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setShowTransactionId(!showTransactionId)}
											className="h-6 w-6 p-0 hover:bg-gray-100"
										>
											{showTransactionId ? (
												<EyeOff className="h-4 w-4 text-gray-500" />
											) : (
												<Eye className="h-4 w-4 text-gray-500" />
											)}
										</Button>
									</div>
								</div>
							</div>

							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">จำนวนเงิน</p>
									<div className="flex items-center gap-2">
										<p className="text-lg font-semibold text-green-600">
											฿{deposit.amount?.toLocaleString()}
										</p>
									</div>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.check className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">สถานะ</p>
									<div className="flex items-center">
										<div
											className={cn(
												'h-2.5 w-2.5 rounded-full',
												deposit.status === 'COMPLETED' && 'bg-green-500',
												deposit.status === 'PENDING' && 'bg-yellow-500',
												deposit.status === 'PROCESSING' && 'bg-amber-500',
												deposit.status === 'FAILED' && 'bg-red-500',
												deposit.status === 'CREATED' && 'bg-blue-500',
												(deposit.status === 'CANCELED' ||
													deposit.status === 'EXPIRED') &&
													'bg-gray-500',
											)}
										/>
										<p
											className={cn(
												'text-lg font-semibold',
												getStatusColor(deposit.status),
											)}
										>
											{getStatusText(deposit.status)}
										</p>
									</div>
								</div>
							</div>
						</div>
						<Separator />
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วิธีการฝาก</p>
									<p className="font-medium">{getMethodText(deposit.method)}</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.check className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วิธีการตรวจสอบ</p>
									<p className="font-medium">
										{getBankValidateMethodText(deposit.bankValidateMethod)}
									</p>
								</div>
							</div>
						</div>
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">ประเภทฝากแมนนวล</p>
									<p className="font-medium">
										{getManualDepositTypeText(deposit.manualDepositType)}
									</p>
								</div>
							</div>
						</div>
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.user className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">ประเภท</p>
									<p className="font-medium">
										{deposit.status === DepositStatus.EXPIRED
											? 'หมดอายุ'
											: deposit.processor === 'AUTO'
												? 'ฝากอัตโนมัติ'
												: `ทำรายการโดยพนักงาน ${deposit.manualDepositOperator?.username ? ` ${deposit.manualDepositOperator.username}` : ''}`}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">บัญชีรับเงิน</p>
									<p className="font-medium">{deposit.bankInfo}</p>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Bank Info */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center justify-between">
							<div className="flex items-center gap-2 text-xl font-semibold">
								<Icons.user className="h-5 w-5" />
								ข้อมูลผู้เล่น
							</div>
							<Link
								to={`/operator/get-player-list/player-detail/${deposit.playerId}`}
								className="flex items-center gap-2 text-sm text-blue-500 hover:text-blue-600"
							>
								<span>ดูข้อมูลผู้เล่น</span>
								<Icons.arrowRight className="h-4 w-4" />
							</Link>
						</h2>
					</div>
					<div className="space-y-6 p-6">
						<div className="grid gap-3 md:grid-cols-2">
							<div className="rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Icons.user className="h-4 w-4 text-gray-500" />
									<div>
										<p className="text-sm text-gray-500">ผู้เล่น</p>
										<p className="font-medium">
											{deposit.player?.username || deposit.player?.phone}
										</p>
									</div>
								</div>
							</div>
							<div className="rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Icons.billing className="h-4 w-4 text-gray-500" />
									<div>
										<p className="text-sm text-gray-500">เลขบัญชี</p>
										<p className="font-medium">
											{deposit.playerBank?.accountNumber}
										</p>
									</div>
								</div>
							</div>
							<div className="rounded-lg border p-4">
								<div className="flex items-center gap-3">
									<Icons.billing className="h-4 w-4 text-gray-500" />
									<div>
										<p className="text-sm text-gray-500">ธนาคาร</p>
										<p className="font-medium">
											{deposit.playerBank?.bankProvider}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Promotion Info */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.billing className="h-5 w-5" />
							ข้อมูลโปรโมชั่น
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">โปรโมชั่น</p>
									<p className="font-medium">{deposit.promotionName || '-'}</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.billing className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">
										โบนัสโปรโมชั่นที่ได้รับ
									</p>
									<p className="font-medium">{deposit.bonusAmount || '-'}</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.refresh className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">เทิร์นโอเวอร์</p>
									<p className="font-medium">{deposit.turnoverAmount || '-'}</p>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Status History */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.refresh className="h-5 w-5" />
							ประวัติสถานะ
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4">
							{deposit.timestamp &&
								Object.entries(deposit.timestamp)
									.sort(
										([, a], [, b]) =>
											new Date(a).getTime() - new Date(b).getTime(),
									)
									.map(([status, time]) => (
										<div key={status} className="flex items-center gap-3">
											<div
												className={cn(
													'h-2.5 w-2.5 rounded-full',
													status === 'COMPLETED' && 'bg-green-500',
													status === 'PENDING' && 'bg-yellow-500',
													status === 'PROCESSING' && 'bg-amber-500',
													status === 'FAILED' && 'bg-red-500',
													status === 'CREATED' && 'bg-blue-500',
													status === 'MANUAL_COMPLETED' && 'bg-green-500',
													status === 'PAYMENT_GATEWAY_FAILED' && 'bg-red-500',
													(status === 'CANCELED' || status === 'EXPIRED') &&
														'bg-gray-500',
												)}
											/>
											<div>
												<p
													className={cn(
														'font-medium',
														getStatusColor(status as DepositStatus),
													)}
												>
													{getStatusText(status)}
												</p>
												<p className="text-sm text-gray-500">
													{formatDate(time)}
												</p>
											</div>
										</div>
									))}
						</div>
					</div>
				</Card>

				{/* Time Information */}
				<Card className="col-span-2 overflow-hidden md:col-span-1">
					<div className="border-b bg-muted/30 p-6">
						<h2 className="flex items-center gap-2 text-xl font-semibold">
							<Icons.billing className="h-5 w-5" />
							ข้อมูลเวลา
						</h2>
					</div>
					<div className="space-y-4 p-6">
						<div className="grid gap-4 md:grid-cols-2">
							<div className="flex items-center gap-3">
								<Icons.add className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วันที่สร้าง</p>
									<p className="font-medium">
										{deposit.createdAt && formatDate(deposit.createdAt)}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.refresh className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">อัพเดทล่าสุด</p>
									<p className="font-medium">
										{deposit.updatedAt && formatDate(deposit.updatedAt)}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.check className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วันที่ตรวจสอบสลิป</p>
									<p className="font-medium">
										{deposit.slipVerifyAt
											? formatDate(deposit.slipVerifyAt)
											: '-'}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Icons.refresh className="h-4 w-4 text-gray-500" />
								<div>
									<p className="text-sm text-gray-500">วันที่หมดอายุ</p>
									<p className="font-medium">
										{deposit.expiredAt && formatDate(deposit.expiredAt)}
									</p>
								</div>
							</div>
						</div>
					</div>
				</Card>

				{/* Slip Verification Info (AUTOSSLIP / VERIFY_SLIP) */}
				{(deposit.slipImageUrl || deposit.slipRejectReason) && (
					<Card className="col-span-2 overflow-hidden">
						<div className="border-b bg-muted/30 p-6">
							<h2 className="flex items-center gap-2 text-xl font-semibold">
								<Icons.billing className="h-5 w-5" />
								ข้อมูลสลิป
							</h2>
						</div>
						<div className="space-y-4 p-6">
							{deposit.slipRejectReason && (
								<div className="rounded-lg border border-red-200 bg-red-50 p-4">
									<p className="text-sm font-medium text-red-800">
										สาเหตุที่สลิปไม่ผ่าน
									</p>
									<p className="mt-1 text-sm text-red-600">
										{deposit.slipRejectReason}
									</p>
								</div>
							)}
							{deposit.slipImageUrl && (
								<div>
									<p className="mb-2 text-sm text-gray-500">
										สลิปที่ลูกค้าอัพโหลด
									</p>
									<a
										href={deposit.slipImageUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										<img
											src={deposit.slipImageUrl}
											alt="สลิปการโอนเงิน"
											className="max-h-96 rounded-lg border object-contain"
										/>
									</a>
								</div>
							)}
						</div>
					</Card>
				)}

				{/* Payment Gateway Errors Table */}
				{deposit.paymentGatewayErrors &&
					deposit.paymentGatewayErrors.length > 0 && (
						<Card className="mt-6 p-6">
							<h3 className="mb-4 text-lg font-semibold">สาเหตุ</h3>
							<div className="overflow-x-auto">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Error ID</TableHead>
											<TableHead>ธนาคารออโต้</TableHead>
											<TableHead>ข้อมูล</TableHead>
											<TableHead>วันที่สร้าง</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{deposit.paymentGatewayErrors.map((error) => (
											<TableRow key={error.id}>
												<TableCell className="font-mono text-xs">
													{error.id}
												</TableCell>
												<TableCell>
													{error.paymentGateway?.bankProviderName || '-'}
												</TableCell>
												<TableCell>{error.errorMessage}</TableCell>
												<TableCell>{formatDate(error.createdAt)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Card>
					)}
			</div>
		</div>
	)
}
